import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  BugReport as BugReportIcon,
  Security as SecurityIcon,
  Nature as NatureIcon,
  Water as WaterIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import api from 'services/api';

const PerilsInformation = () => {
  const [perils, setPerils] = useState([]);
  const [diseases, setDiseases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeril, setSelectedPeril] = useState(null);
  const [selectedDisease, setSelectedDisease] = useState(null);
  const [perilDialogOpen, setPerilDialogOpen] = useState(false);
  const [diseaseDialogOpen, setDiseaseDialogOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [perilsResponse, diseasesResponse] = await Promise.all([
        api.get('/api/v1/perils'),
        api.get('/api/v1/diseases')
      ]);

      if (perilsResponse.data.success) {
        setPerils(perilsResponse.data.data);
      }

      if (diseasesResponse.data.success) {
        setDiseases(diseasesResponse.data.data);
      }
    } catch (err) {
      console.error('Error loading perils and diseases:', err);
      setError('Failed to load perils and diseases information');
    } finally {
      setLoading(false);
    }
  };

  const handlePerilClick = (peril) => {
    setSelectedPeril(peril);
    setPerilDialogOpen(true);
  };

  const handleDiseaseClick = (disease) => {
    setSelectedDisease(disease);
    setDiseaseDialogOpen(true);
  };

  const getPerilIcon = (perilId) => {
    const iconMap = {
      'summer_kill': <WaterIcon color="primary" />,
      'poisoning_third_party': <WarningIcon color="error" />,
      'riot_strike': <SecurityIcon color="warning" />,
      'malicious_acts': <SecurityIcon color="error" />,
      'earthquake': <NatureIcon color="secondary" />,
      'explosion_implosion': <WarningIcon color="error" />,
      'storm_tempest': <NatureIcon color="info" />,
      'volcanic_eruption': <NatureIcon color="error" />,
      'flood_inundation': <WaterIcon color="info" />,
      'aircraft_impact': <WarningIcon color="warning" />,
      'terrorism': <SecurityIcon color="error" />
    };
    return iconMap[perilId] || <WarningIcon color="primary" />;
  };

  const getDiseaseIcon = (diseaseId) => {
    return <BugReportIcon color="error" />;
  };

  const getMortalityColor = (mortalityRate) => {
    if (mortalityRate.includes('80-100%') || mortalityRate.includes('100%')) {
      return 'error';
    } else if (mortalityRate.includes('40-70%') || mortalityRate.includes('70%')) {
      return 'warning';
    } else {
      return 'info';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Box display="flex" alignItems="center" mb={2}>
          <WarningIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1" color="white" fontWeight="bold">
              Perils & Diseases Information
            </Typography>
            <Typography variant="h6" color="white" sx={{ opacity: 0.9 }}>
              Comprehensive guide to insurance coverage and disease management
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SecurityIcon sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {perils.length}
                  </Typography>
                  <Typography variant="body2">
                    Basic Perils Covered
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <BugReportIcon sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {diseases.length}
                  </Typography>
                  <Typography variant="body2">
                    Notified Diseases
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    3
                  </Typography>
                  <Typography variant="body2">
                    Insurance Plans
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <InfoIcon sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    24/7
                  </Typography>
                  <Typography variant="body2">
                    Support Available
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Basic Perils Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom color="primary" fontWeight="bold">
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Basic Perils (Plan A Coverage)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          These perils are covered under all insurance plans (A, B, and C)
        </Typography>
        
        <Grid container spacing={2}>
          {perils.map((peril) => (
            <Grid item xs={12} sm={6} md={4} key={peril.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => handlePerilClick(peril)}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    {getPerilIcon(peril.id)}
                    <Typography variant="h6" component="h3" sx={{ ml: 1, fontWeight: 'bold' }}>
                      {peril.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {peril.description}
                  </Typography>
                  <Chip 
                    label="Basic Peril" 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </CardContent>
                <CardActions>
                  <Button size="small" color="primary">
                    Learn More
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Notified Diseases Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom color="error" fontWeight="bold">
          <BugReportIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Notified Diseases (Plan B & C Coverage)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          These diseases are covered under Plan B and Plan C insurance policies
        </Typography>
        
        <Grid container spacing={2}>
          {diseases.map((disease) => (
            <Grid item xs={12} sm={6} md={4} key={disease.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => handleDiseaseClick(disease)}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    {getDiseaseIcon(disease.id)}
                    <Typography variant="h6" component="h3" sx={{ ml: 1, fontWeight: 'bold' }}>
                      {disease.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {disease.description}
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip 
                      label={disease.mortality_rate} 
                      size="small" 
                      color={getMortalityColor(disease.mortality_rate)}
                      variant="outlined"
                    />
                    <Chip 
                      label="Notified Disease" 
                      size="small" 
                      color="error" 
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
                <CardActions>
                  <Button size="small" color="error">
                    View Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Peril Detail Dialog */}
      <Dialog 
        open={perilDialogOpen} 
        onClose={() => setPerilDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              {selectedPeril && getPerilIcon(selectedPeril.id)}
              <Typography variant="h6" sx={{ ml: 1 }}>
                {selectedPeril?.name}
              </Typography>
            </Box>
            <IconButton onClick={() => setPerilDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPeril && (
            <Box>
              <Typography variant="body1" paragraph>
                {selectedPeril.description}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom color="primary">
                Prevention Measures
              </Typography>
              <Typography variant="body2" paragraph>
                {selectedPeril.prevention}
              </Typography>
              
              <Typography variant="h6" gutterBottom color="warning.main">
                Symptoms to Watch For
              </Typography>
              <Typography variant="body2">
                {selectedPeril.symptoms}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPerilDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disease Detail Dialog */}
      <Dialog 
        open={diseaseDialogOpen} 
        onClose={() => setDiseaseDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              {selectedDisease && getDiseaseIcon(selectedDisease.id)}
              <Typography variant="h6" sx={{ ml: 1 }}>
                {selectedDisease?.name}
              </Typography>
            </Box>
            <IconButton onClick={() => setDiseaseDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedDisease && (
            <Box>
              <Typography variant="body1" paragraph>
                {selectedDisease.description}
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary">
                Scientific Name
              </Typography>
              <Typography variant="body2" paragraph sx={{ fontStyle: 'italic' }}>
                {selectedDisease.scientific_name}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom color="error">
                    Symptoms
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {selectedDisease.symptoms}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom color="success.main">
                    Prevention
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {selectedDisease.prevention}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom color="info.main">
                    Treatment
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {selectedDisease.treatment}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom color="warning.main">
                    Mortality Rate
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {selectedDisease.mortality_rate}
                  </Typography>
                </Grid>
              </Grid>
              
              <Typography variant="h6" gutterBottom color="secondary">
                Seasonal Risk
              </Typography>
              <Typography variant="body2">
                {selectedDisease.seasonal_risk}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiseaseDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PerilsInformation;
